import type {
  BusinessReportOutcome,
  BusinessReportOutcomeDetails,
  BusinessReportOutcomeResult,
} from "@/modules/snapshots/businessReports.types";
import type { SnapshotGridField } from "./types";

/**
 * What a cell edit changed on an outcome. Applied in place to every cached copy of the outcome (grid
 * pages, outcome lists, the detail view) so a single-cell edit never costs a full `grid-query`.
 */
export interface OutcomeCachePatch {
  verified?: boolean;
  name?: string;
  /** Results to upsert by id; a result the cached copy does not have yet (a new note) is appended. */
  results?: BusinessReportOutcomeResult[];
}

export const outcomePatchFromDetails = (details: BusinessReportOutcomeDetails): OutcomeCachePatch => ({
  verified: details.verified,
  name: details.name,
  results: details.result,
});

/** Mutates `outcome` (an Immer draft) with the patch. */
export const applyOutcomePatch = (outcome: BusinessReportOutcome, patch: OutcomeCachePatch): void => {
  if (patch.verified !== undefined) outcome.verified = patch.verified;
  if (patch.name !== undefined) outcome.name = patch.name;
  if (!patch.results) return;
  for (const result of patch.results) {
    const index = outcome.result.findIndex(existing => existing.id === result.id);
    if (index === -1) {
      outcome.result.push(result);
    } else {
      Object.assign(outcome.result[index], result);
    }
  }
};

/** Patches the outcome inside a cached row list; returns whether the list contained it. */
export const patchOutcomeInRows = (
  rows: BusinessReportOutcome[],
  outcomeId: string,
  patch: OutcomeCachePatch,
): boolean => {
  const row = rows.find(existing => existing.id === outcomeId);
  if (!row) return false;
  applyOutcomePatch(row, patch);
  return true;
};

/** Replaces the cached copy of `outcome` with the server's row; returns whether the list contained it. */
export const replaceOutcomeInRows = (rows: BusinessReportOutcome[], outcome: BusinessReportOutcome): boolean => {
  const index = rows.findIndex(existing => existing.id === outcome.id);
  if (index === -1) return false;
  rows[index] = outcome;
  return true;
};

/** A result the single-row endpoint can look the row up by: read-only values have no stored result. */
export const storedResultId = (outcome: BusinessReportOutcome): string | undefined =>
  outcome.result.find(result => !result.read_only)?.id;

/**
 * A note saved into a column the grid page does not list yet (the first note of a collection, a new
 * column name) changes the page's `fields`, which only a full page fetch can provide.
 */
export const patchAddsUnknownNoteColumn = (fields: SnapshotGridField[], patch: OutcomeCachePatch): boolean =>
  (patch.results ?? []).some(
    result => result.is_note && !fields.some(field => field.is_note && field.name === result.name),
  );

/** `PATCH /business-report-outcome/{id}` bodies that only touch cell-level state and can be patched in place. */
export const isInPlaceOutcomePatch = (data: Record<string, unknown>): boolean =>
  Object.keys(data).every(key => key === "verified" || key === "name");
